import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

export function UserMenu() {
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  if (!user) return null;

  const displayName = user.displayName || user.email.split('@')[0];
  const isPro = user.subscriptionTier === 'pro';

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="user-menu-avatar">
          {displayName.charAt(0).toUpperCase()}
        </span>
        <span className="user-menu-name">{displayName}</span>
        {isPro && <span className="user-menu-badge">Pro</span>}
        <svg
          className={`user-menu-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <p className="user-menu-header-name">{displayName}</p>
            <p className="user-menu-header-email">{user.email}</p>
            <p className="user-menu-header-tier">
              {isPro ? 'Pro Plan' : 'Free Plan'}
            </p>
          </div>
          <div className="user-menu-divider" />
          <button
            className="user-menu-item"
            onClick={handleSettings}
            role="menuitem"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 8C13.5 8.34 13.47 8.67 13.42 9L14.92 10.12C15.05 10.22 15.09 10.4 15 10.55L13.6 12.95C13.51 13.1 13.33 13.16 13.17 13.1L11.41 12.39C11 12.7 10.56 12.95 10.08 13.13L9.83 15C9.81 15.17 9.66 15.3 9.48 15.3H6.68C6.5 15.3 6.35 15.17 6.33 15L6.08 13.13C5.6 12.95 5.16 12.7 4.75 12.39L2.99 13.1C2.83 13.16 2.65 13.1 2.56 12.95L1.16 10.55C1.07 10.4 1.11 10.22 1.24 10.12L2.74 9C2.69 8.67 2.66 8.34 2.66 8C2.66 7.66 2.69 7.33 2.74 7L1.24 5.88C1.11 5.78 1.07 5.6 1.16 5.45L2.56 3.05C2.65 2.9 2.83 2.84 2.99 2.9L4.75 3.61C5.16 3.3 5.6 3.05 6.08 2.87L6.33 1C6.35 0.83 6.5 0.7 6.68 0.7H9.48C9.66 0.7 9.81 0.83 9.83 1L10.08 2.87C10.56 3.05 11 3.3 11.41 3.61L13.17 2.9C13.33 2.84 13.51 2.9 13.6 3.05L15 5.45C15.09 5.6 15.05 5.78 14.92 5.88L13.42 7C13.47 7.33 13.5 7.66 13.5 8Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            Settings
          </button>
          <button
            className="user-menu-item user-menu-item-danger"
            onClick={handleLogout}
            role="menuitem"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.6667 11.3333L14 8L10.6667 4.66667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 8H6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
